import { SupportedLocale } from "@/i18n";
import { CollectorNameCF } from "@/lib/compute-field/collector";
import { DimensionsFormatting } from "@/lib/compute-field/shared/dimensions";
import { SupportTechniqueCF } from "@/lib/compute-field/shared/technique";
import { DimensionsDTO } from "@/lib/shared-types";
import { formatTextWithColon, tryParseJson } from "@/lib/utils";
import { SculptureEdition } from "@/lib/types";
import { MergedDisplayData } from "./types";

interface ArtworkSculptureInfoProps {
  sculptureEditionData: any;
  displayData?: MergedDisplayData;
  locale: SupportedLocale;
  t: (key: string) => string;
}

function getLocalized(
  value: unknown,
  locale: SupportedLocale,
): string | undefined {
  if (!value || typeof value !== "object") return undefined;
  const record = value as Record<string, unknown>;
  const v = record[locale];
  if (typeof v === "string" && v.trim().length > 0) return v;
  for (const k of Object.keys(record)) {
    const candidate = record[k];
    if (typeof candidate === "string" && candidate.trim().length > 0)
      return candidate;
  }
  return undefined;
}

function hasValidDimensionValue(field: unknown) {
  return (
    field != undefined &&
    !(Number(field) <= 0) &&
    field !== "undefined" &&
    field !== "0" &&
    field != "" &&
    field != "false" &&
    field != false
  );
}

export function ArtworkSculptureInfo({
  sculptureEditionData,
  displayData,
  locale,
  t,
}: ArtworkSculptureInfoProps) {
  if (!sculptureEditionData) return null;

  const edition = sculptureEditionData as SculptureEdition;

  const technique = edition.sculptureEditionTechnique;
  const support = edition.sculptureEditionSupport;
  const materiau = edition.sculptureEditionMateriau;
  const numerotation = getLocalized(edition.sculptureEditionNumerotation, locale);
  const signature = getLocalized(edition.sculptureEditionSignature, locale);
  const marqueTampons = edition.sculptureEditionMarqueTamponsPublished
    ? getLocalized(edition.sculptureEditionMarqueTampons, locale)
    : undefined;
  const materiauLocal = getLocalized(materiau, locale);

  const hasTechniqueSupport =
    (technique &&
      Object.keys(technique).length > 0 &&
      getLocalized(technique, locale)) ||
    (support &&
      Object.keys(support).length > 0 &&
      getLocalized(support, locale));

  const numeroEnregistrement =
    edition.sculptureEditionNumeroEnregistrement?.trim();
  const parsedDimensions = tryParseJson<DimensionsDTO>(
    edition.sculptureEditionDimensions,
  );
  const shouldShowDimensions =
    !!parsedDimensions &&
    (hasValidDimensionValue(parsedDimensions.cm_width) ||
      hasValidDimensionValue(parsedDimensions.cm_height) ||
      hasValidDimensionValue(parsedDimensions.cm_depth) ||
      hasValidDimensionValue(parsedDimensions.cm_diameter));

  return (
    <div className="my-1 mt-4 flex flex-col gap-2 font-normal front-sans text-sm leading-[110%] subpixel-antialiased">
      {numeroEnregistrement && (
        <p>
          {formatTextWithColon(
            t("sculpture.form.sculptureEditionNumeroEnregistrement"),
            locale,
            numeroEnregistrement,
          )}
        </p>
      )}

      {edition.sculptureEditionNumeroInventaire && (
        <p>
          {formatTextWithColon(
            t("sculpture.form.sculptureEditionNumeroInventaire"),
            locale,
            edition.sculptureEditionNumeroInventaire,
          )}
        </p>
      )}

      {shouldShowDimensions && parsedDimensions && (
        <p>
          {t("sculpture.form.sculptureEditionDimensions") + ": "}
          <DimensionsFormatting
            input={parsedDimensions}
            language={locale}
            lineBreak={false}
          />
        </p>
      )}

      {edition.sculptureEditionTypeEdition && (
        <p>
          {formatTextWithColon(
            t("sculpture.form.sculptureEditionTypeEdition"),
            locale,
            edition.sculptureEditionTypeEdition,
          )}
        </p>
      )}

      {numerotation && (
        <p>
          {formatTextWithColon(
            t("sculpture.form.sculptureEditionNumerotation"),
            locale,
            numerotation,
          )}
        </p>
      )}

      {edition.sculptureEditionDateRealisation && (
        <p>
          {formatTextWithColon(
            t("sculpture.form.sculptureEditionDateRealisation"),
            locale,
            edition.sculptureEditionDateRealisation,
          )}
        </p>
      )}

      {edition.sculptureEditionFondeurFabricant && (
        <p>
          {formatTextWithColon(
            t("sculpture.form.sculptureEditionFondeurFabricant"),
            locale,
            edition.sculptureEditionFondeurFabricant,
          )}
        </p>
      )}

      {edition.sculptureEditionNumeroFonte && (
        <p>
          {formatTextWithColon(
            t("sculpture.form.sculptureEditionNumeroFonte"),
            locale,
            edition.sculptureEditionNumeroFonte,
          )}
        </p>
      )}

      {hasTechniqueSupport && (
        <p>
          {t("singleArtworkSubtitleSculptureEditionTechniqueSupport") + ": "}
          <SupportTechniqueCF
            input={{
              technique: (technique || {}) as Record<string, string>,
              support: (support || {}) as Record<string, string>,
              preposition: t("computeField.techniqueSupportPreposition"),
            }}
            language={locale}
            lineBreak={false}
          />
        </p>
      )}

      {materiauLocal && (
        <p>
          {formatTextWithColon(
            t("sculpture.form.sculptureEditionMateriau"),
            locale,
            materiauLocal,
          )}
        </p>
      )}

      {edition.sculptureEditionDescriptionSocle && (
        <p>
          {formatTextWithColon(
            t("sculpture.form.sculptureEditionDescriptionSocle"),
            locale,
            edition.sculptureEditionDescriptionSocle,
          )}
        </p>
      )}

      {signature && (
        <p>
          {formatTextWithColon(
            t("sculpture.form.sculptureEditionSignature"),
            locale,
            signature,
          )}
        </p>
      )}

      {edition.sculptureEditionMarqueTamponsPublished && marqueTampons && (
        <p>
          {formatTextWithColon(
            t("sculpture.form.sculptureEditionMarqueTampons"),
            locale,
            marqueTampons,
          )}
        </p>
      )}

      {edition.collector && typeof edition.collector === "object" && (
        <p>
          {formatTextWithColon(
            t("sculpture.form.sculptureEditionCollector"),
            locale,
            "",
          )}
          <CollectorNameCF
            input={{
              collectionneursDescriptionNom:
                edition.collector.collectionneursDescriptionNom,
              collectionneursDescriptionPrenom:
                edition.collector.collectionneursDescriptionPrenom,
              collectionneursDescriptionCollectionPublique:
                edition.collector.collectionneursDescriptionCollectionPublique,
              collectionneursDescriptionVille:
                edition.collector.collectionneursDescriptionVille,
              collectionneursDescriptionVillePublished:
                edition.collector.collectionneursDescriptionVillePublished,
              collectionneursDescriptionPays:
                edition.collector.collectionneursDescriptionPays,
              collectionneursDescriptionPaysPublished:
                edition.collector.collectionneursDescriptionPaysPublished,
              showCity: true,
            }}
            language={locale}
          />
        </p>
      )}

      {displayData?.oeuvresCreditsCreditOeuvre && (
        <p>
          {formatTextWithColon(
            t("oeuvresCreditsCreditOeuvre"),
            locale,
            displayData.oeuvresCreditsCreditOeuvre,
          )}
        </p>
      )}
    </div>
  );
}
